
import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, query, where, onSnapshot, getDocs 
} from 'firebase/firestore';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, BarChart, Bar, Legend, Cell, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ReferenceLine, ComposedChart
} from 'recharts';
import { 
  TrendingUp, Users, Target, BookOpen, 
  Filter, Calendar, ChevronDown, Download,
  LayoutDashboard, Search, User, X,
  ArrowUpRight, Award, Brain, BarChart3,
  GitCompare, Plus, Layers, Book
} from 'lucide-react';
import './DashboardEvolucional.css';

const GRADES = ['6º ano', '7º ano', '8º ano', '9º ano'];
const CYCLES = ['Ciclo 1', 'Ciclo 2'];
const YEARS = ['2024', '2025', '2026'];
const AREAS = [
  { id: 'media', label: 'Média Geral' },
  { id: 'lc', label: 'Linguagens' },
  { id: 'mt', label: 'Matemática' },
  { id: 'cn', label: 'Ciências Natureza' },
  { id: 'ch', label: 'Ciências Humanas' }
];

const DashboardEvolucional = ({ db, auth, session }) => {
  const [activeTab, setActiveTab] = useState('macro'); // 'macro', 'individual', 'skills'
  const [selectedArea, setSelectedArea] = useState('media');
  
  const [selA, setSelA] = useState({ year: '2026', grade: '6º ano', cycle: 'Ciclo 1', active: true });
  const [selB, setSelB] = useState({ year: '2025', grade: '6º ano', cycle: 'Ciclo 1', active: true });
  const [selC, setSelC] = useState({ year: '2024', grade: '6º ano', cycle: 'Ciclo 1', active: false });

  const [performances, setPerformances] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db) return;
    setLoading(true);
    const qPerf = query(collection(db, 'performances'));
    const unsubPerf = onSnapshot(qPerf, (snapshot) => {
      setPerformances(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const qStudents = query(collection(db, 'students'));
    const unsubStudents = onSnapshot(qStudents, (snapshot) => {
      setStudents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => { unsubPerf(); unsubStudents(); };
  }, [db]);

  const normalizeValue = (val) => {
    let clean = val.toString().replace('%', '').replace(',', '.').trim();
    let parsed = parseFloat(clean);
    if (isNaN(parsed)) return 0;
    // Se for decimal entre 0 e 1, normaliza para 0-100 (ex: 0.85 -> 85)
    if (parsed > 0 && parsed <= 1) {
        return Math.round(parsed * 100);
    }
    return Math.round(parsed);
  };

  const normalizeCycle = (c) => {
     if (!c) return '';
     const cStr = String(c);
     if (cStr.includes('1')) return 'Ciclo 1';
     if (cStr.includes('2')) return 'Ciclo 2';
     return cStr;
  };

  const getStats = (sel) => {
      if (!sel.active) return null;
      const data = performances.filter(p => 
          p.year?.toString() === sel.year && 
          p.grade === parseInt(sel.grade) &&
          normalizeCycle(p.cycle) === sel.cycle
      );
      if (data.length === 0) return { tri: 0, acertos: 0, count: 0, raw: [] };
      const areaKey = selectedArea === 'media' ? 'media_acertos' : `perc_${selectedArea}`;
      return {
         tri: Math.round(data.reduce((acc, curr) => acc + (curr.scores?.[selectedArea] || 0), 0) / (data.length || 1)),
         acertos: Math.round(data.reduce((acc, curr) => acc + (curr.scores?.[areaKey] || 0), 0) / (data.length || 1)),
         count: data.length,
         raw: data
      };
  };

  const statsA = useMemo(() => getStats(selA), [performances, selA, selectedArea]);
  const statsB = useMemo(() => getStats(selB), [performances, selB, selectedArea]);
  const statsC = useMemo(() => getStats(selC), [performances, selC, selectedArea]);

  const comparisonChartData = useMemo(() => {
     const data = [];
     if (statsA && statsA.tri > 0) data.push({ name: `(A) ${selA.year} ${selA.cycle}`, tri: statsA.tri });
     if (statsB && statsB.tri > 0) data.push({ name: `(B) ${selB.year} ${selB.cycle}`, tri: statsB.tri });
     if (statsC && statsC.tri > 0) data.push({ name: `(C) ${selC.year} ${selC.cycle}`, tri: statsC.tri });
     return data;
  }, [statsA, statsB, statsC, selA, selB, selC]);

  const radarData = useMemo(() => {
    const subjects = AREAS.filter(a => a.id !== 'media');
    return subjects.map(s => {
       const point = { subject: s.label, fullMark: 1000 };
       if (statsA) point.A = Math.round(statsA.raw.reduce((acc, curr) => acc + (curr.scores?.[s.id] || 0), 0) / (statsA.raw.length || 1));
       if (statsB) point.B = Math.round(statsB.raw.reduce((acc, curr) => acc + (curr.scores?.[s.id] || 0), 0) / (statsB.raw.length || 1));
       if (statsC) point.C = Math.round(statsC.raw.reduce((acc, curr) => acc + (curr.scores?.[s.id] || 0), 0) / (statsC.raw.length || 1));
       return point;
    });
  }, [statsA, statsB, statsC]);

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return [];
    const term = searchTerm.toLowerCase();
    return students.filter(s => String(s.name || '').toLowerCase().includes(term)).slice(0, 10);
  }, [students, searchTerm]);

  const studentPerformance = useMemo(() => {
    if (!selectedStudent) return [];
    return performances
      .filter(p => p.studentId === selectedStudent.id)
      .sort((a, b) => {
          const valA = (Number(a.year) || 0) * 10 + (normalizeCycle(a.cycle) === 'Ciclo 2' ? 2 : 1);
          const valB = (Number(b.year) || 0) * 10 + (normalizeCycle(b.cycle) === 'Ciclo 2' ? 2 : 1);
          return valA - valB;
      });
  }, [performances, selectedStudent]);

  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-50 text-indigo-600 font-black animate-pulse">CARREGANDO ENGINE DE DADOS...</div>;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header border-b pb-6 mb-10">
        <div className="dashboard-title">
          <div className="flex items-center gap-4">
             <div className="w-14 h-14 bg-slate-900 text-white rounded-2xl shadow-2xl flex items-center justify-center">
                <BarChart3 size={28} />
             </div>
             <div>
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">Evolucional Analytics</h1>
                <p className="text-sm text-slate-400 font-bold uppercase tracking-wider">Unidade Eleva Barra</p>
             </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-inner border border-slate-200">
            <button 
              onClick={() => setActiveTab('macro')}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-black transition-all ${activeTab === 'macro' ? 'bg-white text-indigo-600 shadow-xl' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <LayoutDashboard size={14} /> COMPARATIVO
            </button>
            <button 
              onClick={() => setActiveTab('skills')}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-black transition-all ${activeTab === 'skills' ? 'bg-white text-indigo-600 shadow-xl' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Award size={14} /> HABILIDADES
            </button>
            <button 
              onClick={() => setActiveTab('individual')}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-black transition-all ${activeTab === 'individual' ? 'bg-white text-indigo-600 shadow-xl' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <User size={14} /> ALUNO
            </button>
          </div>
        </div>
      </header>

      {activeTab === 'macro' && (
        <div className="animate-in fade-in duration-500 space-y-8">
           {/* FILTRO DE ÁREA GLOBAL */}
           <div className="flex justify-center mb-4">
              <div className="bg-white px-6 py-4 rounded-[2rem] shadow-xl border border-slate-100 flex items-center gap-6">
                 <div className="flex items-center gap-3 text-indigo-600 font-black uppercase text-[10px] tracking-widest">
                    <Book size={18} /> Área de Conhecimento:
                 </div>
                 <div className="flex gap-2">
                    {AREAS.map(area => (
                       <button 
                          key={area.id}
                          onClick={() => setSelectedArea(area.id)}
                          className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-tighter transition-all ${selectedArea === area.id ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                       >
                          {area.label}
                       </button>
                    ))}
                 </div>
              </div>
           </div>

           {/* PAINEL DE CONTROLE DE COMPARAÇÃO */}
           <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ComparisonSelector id="A" sel={selA} onChange={setSelA} color="bg-indigo-600" activeColor="#4f46e5" />
              <ComparisonSelector id="B" sel={selB} onChange={setSelB} color="bg-rose-500" activeColor="#f43f5e" />
              {selC.active ? (
                 <ComparisonSelector id="C" sel={selC} onChange={setSelC} color="bg-emerald-500" activeColor="#10b981" onClose={() => setSelC({...selC, active: false})} />
              ) : (
                 <button onClick={() => setSelC({...selC, active: true})} className="h-full border-4 border-dashed border-slate-200 rounded-[2.5rem] flex flex-col items-center justify-center p-8 text-slate-300 hover:border-emerald-400 hover:text-emerald-400 transition-all bg-white/50">
                    <Plus size={48} />
                    <p className="font-black text-xs uppercase mt-3">Adicionar Terceira Via</p>
                 </button>
              )}
           </div>

           {/* KPIs LADO A LADO */}
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <MultiKPICard title={`Média ${AREAS.find(a=>a.id===selectedArea).label} (TRI)`} valA={statsA?.tri} valB={statsB?.tri} valC={selC.active ? statsC?.tri : null} icon={<Target />} colors={['text-indigo-600', 'text-rose-500', 'text-emerald-500']} />
              <MultiKPICard title="% Acertos" valA={statsA?.acertos} valB={statsB?.acertos} valC={selC.active ? statsC?.acertos : null} suffix="%" icon={<Award />} colors={['text-indigo-600', 'text-rose-500', 'text-emerald-500']} />
              <MultiKPICard title="Total Amostra" valA={statsA?.count} valB={statsB?.count} valC={selC.active ? statsC?.count : null} icon={<Users />} colors={['text-indigo-600', 'text-rose-500', 'text-emerald-500']} />
           </div>

           {/* QUADRO DE GRÁFICOS */}
           <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 chart-card p-10 shadow-2xl bg-white border border-slate-100">
                 <div className="flex justify-between items-center mb-10">
                    <h3 className="text-2xl font-black text-slate-800 tracking-tight">Performance por Ano/Ciclo</h3>
                    <div className="text-[10px] uppercase font-black text-slate-300 tracking-widest">{AREAS.find(a=>a.id===selectedArea).label}</div>
                 </div>
                 <div style={{ width: '100%', height: 450 }}>
                    <ResponsiveContainer>
                       <BarChart data={comparisonChartData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 'black'}} />
                          <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} domain={[0, 600]} />
                          <Tooltip cursor={{fill: '#f8fafc'}} contentStyle={{borderRadius: '2rem', border: 'none'}} />
                          <Bar dataKey="tri" radius={[15, 15, 0, 0]} barSize={80}>
                             <Cell fill="#4f46e5" />
                             <Cell fill="#f43f5e" />
                             {selC.active && <Cell fill="#10b981" />}
                          </Bar>
                       </BarChart>
                    </ResponsiveContainer>
                 </div>
              </div>
              <div className="lg:col-span-4 chart-card p-10 shadow-2xl bg-white border border-slate-100">
                 <h3 className="text-xl font-black text-slate-800 mb-8">Raio-X Áreas</h3>
                 <div style={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer>
                       <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: 11, fontWeight: 'black' }} />
                          <Radar name="(A)" dataKey="A" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.4} />
                          <Radar name="(B)" dataKey="B" stroke="#f43f5e" fill="#f43f5e" fillOpacity={0.3} />
                          {selC.active && <Radar name="(C)" dataKey="C" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />}
                          <Tooltip />
                       </RadarChart>
                    </ResponsiveContainer>
                 </div>
              </div>
           </div>
        </div>
      )}

      {activeTab === 'skills' && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-10">
           <div className="bg-white p-12 rounded-[3.5rem] shadow-2xl border border-slate-50">
              <div className="flex justify-between items-end mb-16">
                 <div>
                    <h3 className="text-4xl font-black text-slate-800 tracking-tight">Mapa de Habilidades</h3>
                    <p className="text-slate-400 font-bold mt-2">Detalhamento por Descritores e Competências</p>
                 </div>
                 <div className="flex gap-4">
                    <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest"><div className="w-2 h-2 bg-indigo-600 rounded-full animate-pulse"></div> Seleção A</div>
                    {selB.active && <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 text-rose-500 rounded-full text-[10px] font-black uppercase tracking-widest"><div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></div> Seleção B</div>}
                    {selC.active && <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-500 rounded-full text-[10px] font-black uppercase tracking-widest"><div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div> Seleção C</div>}
                 </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                 {(() => {
                    const allSkills = new Set();
                    [statsA, statsB, statsC].forEach(s => {
                       if (s?.raw) s.raw.forEach(r => Object.keys(r.skills || {}).forEach(k => allSkills.add(k)));
                    });
                    
                    return Array.from(allSkills).sort().map(skill => {
                       const getSkillAvg = (stats) => {
                          if (!stats?.raw || stats.raw.length === 0) return 0;
                          const sum = stats.raw.reduce((acc, curr) => acc + (curr.skills?.[skill] || 0), 0);
                          return Math.round(sum / stats.raw.length);
                       };

                       const valA = getSkillAvg(statsA);
                       const valB = selB.active ? getSkillAvg(statsB) : null;
                       const valC = selC.active ? getSkillAvg(statsC) : null;

                       return (
                          <div key={skill} className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 transition-all hover:scale-105 group">
                             <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 group-hover:text-indigo-600 truncate">{skill}</p>
                             <div className="space-y-3">
                                <SkillMiniBar label="A" val={valA} color="bg-indigo-600" />
                                {valB !== null && <SkillMiniBar label="B" val={valB} color="bg-rose-500" />}
                                {valC !== null && <SkillMiniBar label="C" val={valC} color="bg-emerald-500" />}
                             </div>
                          </div>
                       );
                    });
                 })()}
              </div>
           </div>
        </div>
      )}

      {activeTab === 'individual' && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
           {/* Busca do Aluno */}
           <div className="max-w-4xl mx-auto mb-16 text-center">
              <div className="relative group">
                 <input 
                    type="text" 
                    placeholder="Busca por nome do estudante..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white rounded-[3rem] px-10 py-8 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] border border-slate-100 text-2xl font-black text-slate-800 outline-none placeholder:text-slate-200 transition-all focus:ring-8 focus:ring-indigo-50"
                 />
                 <div className="absolute right-6 top-1/2 -translate-y-1/2 bg-slate-900 text-white p-4 rounded-full shadow-lg">
                    <Search size={28} />
                 </div>
                 {filteredStudents.length > 0 && (
                   <div className="absolute top-28 left-0 w-full bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 z-50 overflow-hidden text-left">
                     {filteredStudents.map(student => (
                       <button key={student.id} onClick={() => { setSelectedStudent(student); setSearchTerm(''); }} className="w-full p-8 hover:bg-slate-50 transition-all flex items-center justify-between border-b last:border-0 border-slate-50 group">
                          <div>
                             <p className="font-black text-slate-800 text-2xl group-hover:text-indigo-600 transition-colors">{student.name}</p>
                             <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.3em] mt-1">Série: {student.lastGrade || '---'}º ano • RA: {student.ra || '---'}</p>
                          </div>
                          <ChevronDown className="-rotate-90 text-slate-200 group-hover:text-indigo-600 transition-all" />
                       </button>
                     ))}
                   </div>
                 )}
              </div>
           </div>

           {selectedStudent ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                 <div className="lg:col-span-8 chart-card p-12 bg-white shadow-2xl rounded-[3rem]">
                    <h3 className="text-3xl font-black text-slate-800 mb-12">Curva de Aprendizagem ({AREAS.find(a=>a.id===selectedArea)?.label || ''})</h3>
                    <div style={{ width: '100%', height: 500 }}>
                       {studentPerformance.length > 0 ? (
                          <ResponsiveContainer>
                             <LineChart data={studentPerformance}>
                                <CartesianGrid strokeDasharray="5 5" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="simulationId" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 'black', fill: '#cbd5e1'}} />
                                <YAxis domain={[0, 1000]} axisLine={false} tickLine={false} tick={{fill: '#cbd5e1'}} />
                                <Tooltip contentStyle={{borderRadius: '2rem', border: 'none'}} />
                                <Line type="monotone" dataKey={`scores.${selectedArea}`} stroke="#4f46e5" strokeWidth={8} dot={{r: 10, fill: '#4f46e5', stroke: '#fff', strokeWidth: 4}} activeDot={{r: 12}} />
                             </LineChart>
                          </ResponsiveContainer>
                       ) : (
                          <div className="flex items-center justify-center h-full text-slate-300 font-black tracking-widest text-sm uppercase">
                             Nenhuma performance registrada
                          </div>
                       )}
                    </div>
                 </div>
                 <div className="lg:col-span-4 chart-card p-12 bg-slate-900 text-white rounded-[3rem] relative overflow-hidden flex flex-col justify-center">
                    <div className="absolute top-0 right-0 p-12 opacity-5"><Brain size={200} /></div>
                    <p className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-400 mb-4 text-center">Resumo Atual</p>
                    <h4 className="text-3xl font-black text-center leading-tight mb-12">{selectedStudent?.name || 'Aluno'}</h4>
                    <div className="space-y-10">
                       <div className="text-center">
                          <p className="text-[10px] font-black uppercase text-slate-500 mb-2">TRI {AREAS.find(a=>a.id===selectedArea)?.label || ''}</p>
                          <p className="text-6xl font-black text-indigo-400">
                             {studentPerformance.length > 0 ? (studentPerformance[studentPerformance.length-1]?.scores?.[selectedArea] || '--') : '--'}
                          </p>
                       </div>
                    </div>
                 </div>
              </div>
           ) : (
              <div className="flex flex-col items-center justify-center py-40 opacity-10 font-black text-3xl">BUSQUE UM ALUNO</div>
           )}
        </div>
      )}
    </div>
  );
};

const ComparisonSelector = ({ id, sel, onChange, color, activeColor, onClose }) => (
  <div className="relative pt-12 pb-8 px-8 bg-white border border-slate-50 rounded-[3rem] shadow-2xl transition-all hover:ring-8 hover:ring-slate-50">
    <div className={`absolute top-0 left-0 right-0 h-16 ${color} rounded-t-[3rem] flex items-center justify-center`}>
       <span className="text-white font-black uppercase tracking-[0.3em] text-xs">Seleção {id}</span>
       {onClose && <button onClick={onClose} className="absolute right-6 bg-white/20 p-1 rounded-full"><X size={14} /></button>}
    </div>
    <div className="grid grid-cols-1 gap-4 mt-8">
       <div>
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Ano</label>
          <select value={sel.year} onChange={(e) => onChange({...sel, year: e.target.value})} className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black outline-none focus:ring-4 focus:ring-indigo-50">
             {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
       </div>
       <div className="grid grid-cols-2 gap-4">
          <div>
             <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Série</label>
             <select value={sel.grade} onChange={(e) => onChange({...sel, grade: e.target.value})} className="w-full bg-slate-50 border-none rounded-2xl p-4 text-xs font-black outline-none">
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
             </select>
          </div>
          <div>
             <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Ciclo</label>
             <select value={sel.cycle} onChange={(e) => onChange({...sel, cycle: e.target.value})} className="w-full bg-slate-50 border-none rounded-2xl p-4 text-xs font-black outline-none">
                {CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
             </select>
          </div>
       </div>
    </div>
  </div>
);

const MultiKPICard = ({ title, valA, valB, valC, suffix = '', icon, colors }) => (
  <div className="chart-card p-10 bg-white shadow-2xl flex flex-col items-center border border-slate-50">
     <div className="bg-slate-50 p-5 rounded-3xl text-slate-900 mb-6">{icon}</div>
     <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-8">{title}</h4>
     <div className="flex items-center gap-8">
        <KPISub value={valA} label="A" color={colors[0]} suffix={suffix} />
        {valB !== null && <KPISub value={valB} label="B" color={colors[1]} suffix={suffix} />}
        {valC !== null && <KPISub value={valC} label="C" color={colors[2]} suffix={suffix} />}
     </div>
  </div>
);

const KPISub = ({ value, label, color, suffix }) => (
  <div className="text-center group">
     <div className={`text-3xl font-black ${color} group-hover:scale-110 transition-transform`}>{value}{suffix}</div>
     <div className="text-[9px] font-bold text-slate-300 mt-1 uppercase tracking-widest">Base {label}</div>
  </div>
);

const SkillMiniBar = ({ label, val, color }) => (
  <div className="flex items-center gap-3">
     <div className="text-[8px] font-black text-slate-400 w-2">{label}</div>
     <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-1000`} style={{ width: `${val}%` }}></div>
     </div>
     <div className="text-[9px] font-black text-slate-600 w-6 text-right">{val}%</div>
  </div>
);

export default DashboardEvolucional;
